import { FC, JSX, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Slide } from '@player/content';
import Input from '@semcore/ui/input';

import { useUploadFileMutation } from '@/entities/content/api/contentApi';
import { Tooltip } from '@/shared';
import { config } from '@/shared/lib/config';
import { cacheFileForUrl } from '@/shared/lib/serviceWorker';
import { toastError } from '@/shared/lib/toastManager';
import Button from '@/shared/ui/button';
import DeleteIcon from '@/shared/ui/icons/Delete';
import RefreshIcon from '@/shared/ui/icons/Refresh';

import { FileSelector } from '../FileSelector';

export interface ISlideTileProps {
  index: number;
  onRemoveClick?: () => void;
}

export const SlideTile: FC<ISlideTileProps> = ({
  index,
  onRemoveClick,
}): JSX.Element => {
  const { control, getValues, setValue, formState } = useFormContext<{
    slides: Slide[];
  }>();
  const [uploadFile] = useUploadFileMutation();

  // Watch the form value for image URL
  const imageUrl = useWatch({
    control,
    name: `slides.${index}.image`,
  });

  // Local state for image preview
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState(false);

  const imageSrc = useMemo(() => {
    if (imageFile) {
      return URL.createObjectURL(imageFile);
    }
    return imageUrl;
  }, [imageFile, imageUrl]);

  const resetImageHandler = () => {
    setValue(`slides.${index}.image`, '');
    setImageFile(null);
    setUploadError(false);
  };

  const onFileSelectedHandler = async (file: File | null) => {
    setValue(`slides.${index}.image`, '');
    setImageFile(file);
    setUploadError(false);

    if (!file) {
      return;
    }

    try {
      const response = await uploadFile({ file, type: 'slide' }).unwrap();
      if (response?.data) {
        const mediaUrl = `${config.MEDIA_CONTENT_URL}${response.data.path}`;

        // Cache the file in SW so it's available immediately (CDN takes ~30s)
        try {
          await cacheFileForUrl(mediaUrl, file);
        } catch (cacheError) {
          console.warn('Failed to cache file in service worker:', cacheError);
          // Continue even if caching fails - the local blob preview will work
        }

        setValue(`slides.${index}.image`, mediaUrl);
        setImageFile(null);
        setUploadError(false);
      }
    } catch (error) {
      setUploadError(true);
      toastError(
        'Failed to upload image. Please try again.',
        'slideUploadFailed'
      );
    }
  };

  const titleForAlt = getValues(`slides.${index}.title`) || 'Slide image';

  return (
    <div className="group flex rounded-lg bg-white p-4">
      <div className="flex flex-col gap-4">
        <div className="relative flex h-[158px] w-[280px] flex-col overflow-hidden rounded-lg">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={titleForAlt}
              className={`h-full w-full object-cover ${uploadError ? 'opacity-50' : ''}`}
              width="100%"
              height="100%"
              referrerPolicy="no-referrer"
            />
          ) : (
            <>
              <FileSelector onFileSelected={onFileSelectedHandler} />
              {formState.errors?.slides?.[index]?.image?.message ? (
                <p className="mt-2 self-end text-red">
                  {formState.errors?.slides?.[index]?.image?.message}
                </p>
              ) : null}
            </>
          )}
          <div className="absolute left-0 right-0 top-0 flex w-full justify-end gap-2 p-2 opacity-0 transition-all group-hover:opacity-100">
            {imageSrc && (
              <Tooltip placement="bottom" title="Replacing graphic content">
                <Button
                  variant="function"
                  size="sm"
                  className="!h-[26px] !w-[26px] !rounded-full !border-0 !bg-white"
                  title={
                    <RefreshIcon className="stroke-black hover:stroke-black" />
                  }
                  onClick={resetImageHandler}
                />
              </Tooltip>
            )}

            {index !== 0 && onRemoveClick && (
              <Tooltip placement="bottom" title="Remove card">
                <Button
                  variant="function"
                  size="sm"
                  className="!h-[26px] !w-[26px] !rounded-full !border-0 !bg-white"
                  title={
                    <DeleteIcon className="stroke-black hover:stroke-black" />
                  }
                  onClick={onRemoveClick}
                />
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Controller
            control={control}
            name={`slides.${index}.title`}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className="flex flex-col gap-1">
                <Input
                  size="l"
                  state={error ? 'invalid' : 'normal'}
                  className="text-base font-light"
                >
                  <Input.Value
                    value={value ?? ''}
                    placeholder="Title"
                    onChange={onChange}
                  />
                </Input>
                {error?.message && (
                  <span className="text-red-500 text-sm">{error.message}</span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name={`slides.${index}.link`}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className="flex flex-col gap-1">
                <Input
                  size="l"
                  state={error ? 'invalid' : 'normal'}
                  className="text-base font-light"
                >
                  <Input.Value
                    value={value ?? ''}
                    placeholder="URL"
                    onChange={onChange}
                  />
                </Input>
                {error?.message && (
                  <span className="text-red-500 text-sm">{error.message}</span>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SlideTile;
